import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useMemo,
  useState,
} from "react";
import { THOR } from "rujira.js";
import { isV2Enabled } from "../redacted/v2/config";
import { readPrivateSessionIntent } from "../redacted/v2/sessionIntent";
import { readPrivateSessionPolicy } from "../redacted/v2/sessionPolicy";
import { AccountLayerProvider, type AccountLayer } from "./accountLayer";
import {
  AccountCapabilitiesProvider,
  CONNECTED_WALLET_CAPABILITIES,
  type AccountCapabilities,
} from "./accountCapabilities";

/**
 * In Private Mode every action runs from the Spending account as a relayed
 * private call. That account cannot sign Cosmos authz grants (the deployed
 * sub-wallet does not expose Authz), so automations such as auto-claim are
 * unavailable; native liquid-staking compounding and contract-driven
 * recurring orders do not need a grant. Its actions also land seconds after
 * their review (proof, optional undo window, first-funding pause, block), so
 * limits that move with prices keep 0.5% headroom.
 */
const SPENDING_ACCOUNT_CAPABILITIES: AccountCapabilities = {
  authzGrants: false,
  priceLimitHeadroomBps: 50n,
};

export interface RedactedProvider {
  isPrivateMode: boolean;
  stealth: string | null; // removed 0x hex string
  setStealth: (stealth: string) => void;
  subWalletAddress: string | null;
  setSubWalletAddress: (subWalletAddress: string) => void;
  publicAddress: string | null;
  setPublicAddress: (address: string) => void;
  setIsPrivateMode: (isPrivateMode: boolean) => void;
  updateRedactedFlag: boolean;
  updateRedactedFlagData: () => void;
  encryptionSignature: string | null;
  setEncryptionSignature: (encryptionSignature: string) => void;
  // getEncryptionSignature: (addressParam?: string | undefined, provider?: Provider.Key | undefined) => Promise<string | null>;
}

const ERROR = () => {
  throw new Error("AccountProvider Context not defined");
};

const Context = createContext<RedactedProvider>({
  isPrivateMode: false,
  setIsPrivateMode: ERROR,
  stealth: null,
  setStealth: ERROR,
  subWalletAddress: null,
  setSubWalletAddress: ERROR,
  publicAddress: null,
  setPublicAddress: ERROR,
  updateRedactedFlag: false,
  updateRedactedFlagData: ERROR,
  encryptionSignature: null,
  setEncryptionSignature: ERROR,
});

export const RedactedContext: FC<PropsWithChildren> = ({ children }) => {
  const [stealth, setStealth] = useState<string>("");
  const [publicAddress, setPublicAddress] = useState<string>("");
  const [subWalletAddress, setSubWalletAddress] = useState<string>("");
  // Preserve the user's privacy choice while the separate session vault is
  // restored. A locked private session must never silently become a public send.
  const [isPrivateMode, setIsPrivateMode] = useState(
    () =>
      isV2Enabled() &&
      (readPrivateSessionIntent() || readPrivateSessionPolicy().alwaysPrivate)
  );
  const [updateRedactedFlag, setUpdateRedactedFlag] = useState<boolean>(false);
  // Session-only wallet derivation signature used for local proof generation.
  // This must never be persisted or sent to the backend.
  const [encryptionSignature, setEncryptionSignature] = useState<string | null>(
    null
  );

  const updateRedactedFlagData = () => {
    // Recovery and deposit confirmation can arrive close together. A
    // functional update makes every invalidation observable by Relay instead
    // of letting two callbacks close over the same previous boolean.
    setUpdateRedactedFlag((current) => !current);
  };

  return (
    <Context.Provider
      value={{
        stealth,
        setStealth,
        subWalletAddress,
        setSubWalletAddress,
        publicAddress,
        setPublicAddress,
        setIsPrivateMode,
        isPrivateMode,
        updateRedactedFlag,
        updateRedactedFlagData,
        encryptionSignature,
        setEncryptionSignature,
      }}>
      <AccountCapabilitiesProvider
        value={
          isPrivateMode
            ? SPENDING_ACCOUNT_CAPABILITIES
            : CONNECTED_WALLET_CAPABILITIES
        }>
        <SpendingAccountLayer>{children}</SpendingAccountLayer>
      </AccountCapabilitiesProvider>
    </Context.Provider>
  );
};

export const useRedacted = () => useContext(Context);

/**
 * Private Mode shows the Spending account: features act as it (see
 * AccountLayer), and it never pays a connected wallet. While it is not
 * available (locked, restoring) the wallet stands in, under the same rule.
 */
const SpendingAccountLayer: FC<PropsWithChildren> = ({ children }) => {
  const { isPrivateMode, subWalletAddress, publicAddress } =
    useContext(Context);
  const layer = useMemo<AccountLayer | undefined>(
    () =>
      isPrivateMode
        ? {
            unlinked: true,
            account: subWalletAddress
              ? {
                  address: { address: subWalletAddress, networks: [THOR] },
                  owner: publicAddress || undefined,
                }
              : undefined,
          }
        : undefined,
    [isPrivateMode, publicAddress, subWalletAddress]
  );
  return <AccountLayerProvider layer={layer}>{children}</AccountLayerProvider>;
};

/**
 * Model B: render a subtree for one owned sub (e.g. a position in the
 * portfolio) without changing the app-wide private scope.
 */
export const RedactedScope: FC<
  PropsWithChildren<{ subWalletAddress: string }>
> = ({ subWalletAddress, children }) => {
  const parent = useContext(Context);
  const value = useMemo(
    () => ({ ...parent, subWalletAddress }),
    [parent, subWalletAddress]
  );
  return (
    <Context.Provider value={value}>
      <SpendingAccountLayer>{children}</SpendingAccountLayer>
    </Context.Provider>
  );
};
